/**
 * Ausgabedatei der Richtlinie herunterladen — Word, PDF oder NISO-STS, erzeugt vom Server aus dem
 * Blockmodell (veröffentlichter Stand, ohne Entwürfe).
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "../components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "../components/ui/dropdown-menu";
import { useGuidelinesAPI, type ExportFormat } from "./GuidelinesAPI";

const FORMATS: ExportFormat[] = ["docx", "pdf", "sts"];

export const saveBlob = (blob: Blob, name: string) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

export function ExportMenu(props: { customer: string; documentId: string }) {
  const { t } = useTranslation();
  const api = useGuidelinesAPI(props.customer);
  const [busy, setBusy] = useState<ExportFormat | null>(null);

  const run = async (format: ExportFormat) => {
    setBusy(format);
    try {
      const { blob, name } = await api.exportDocument(props.documentId, format);
      saveBlob(blob, name);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant="outline" disabled={!!busy} aria-label={t("guidelinesPage.export.button")}>
          <Download size={14} className={busy ? "mr-1 animate-pulse" : "mr-1"} />
          {busy ? t("guidelinesPage.export.busy") : t("guidelinesPage.export.button")}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {FORMATS.map((f) => (
          <DropdownMenuItem key={f} onSelect={() => run(f)} data-export={f}>
            {t(`guidelinesPage.export.${f}`)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
